import React, { useEffect } from "react";
import { useParams, Navigate } from "react-router-dom";
import LandingPage from "@/pages/LandingPage";
import { VILLES } from "@/data/ville_content";
import { METIER_IMAGES } from "@/data/metier_images";

const METIER_DATA = {
  plombier: {
    title: "Plombier",
    hook: "On vous trouve des nouveaux clients pendant que vous êtes sous l'évier",
    subtitle: "Appels d'urgence manqués, devis non relancés, planning vide — notre IA gère tout pour vous.",
    stats: { clients: "+15", label: "Urgences/mois" },
    keywords: "plombier, dépannage, sanitaire, urgence plomberie, plombier Suisse",
    metaTitle: "Lead Artisan pour Plombiers — Plus de clients, zéro appel manqué",
    metaDesc: "Solution IA pour plombiers en Suisse. Trouvez de nouveaux clients, répondez aux urgences 24/7, relancez vos devis automatiquement.",
  },
  electricien: {
    title: "Électricien",
    hook: "On vous trouve des nouveaux chantiers pendant que vous êtes au tableau",
    subtitle: "Installations, dépannages, rénovations — ne ratez plus aucune demande.",
    stats: { clients: "+12", label: "Chantiers/mois" },
    keywords: "électricien, installation électrique, dépannage électrique, électricien Suisse",
    metaTitle: "Lead Artisan pour Électriciens — Remplissez votre planning automatiquement",
    metaDesc: "Solution IA pour électriciens en Suisse. Acquisition de nouveaux chantiers, call bot 24/7, relances devis et factures.",
  },
  serrurier: {
    title: "Serrurier",
    hook: "On vous trouve des ouvertures de porte pendant que vous êtes en intervention",
    subtitle: "Les urgences n'attendent pas. Notre IA répond 24/7 et vous transmet les leads chauds.",
    stats: { clients: "+20", label: "Urgences/mois" },
    keywords: "serrurier, ouverture de porte, dépannage serrurerie, serrurier urgence Suisse",
    metaTitle: "Lead Artisan pour Serruriers — Captez chaque urgence 24/7",
    metaDesc: "Solution IA pour serruriers en Suisse. Répondez à chaque appel d'urgence, même la nuit. Plus de clients, plus de CA.",
  },
  chauffagiste: {
    title: "Chauffagiste",
    hook: "On vous trouve des installations pendant que vous êtes en maintenance",
    subtitle: "Chaudières, pompes à chaleur, entretien — remplissez votre planning automatiquement.",
    stats: { clients: "+10", label: "Installations/mois" },
    keywords: "chauffagiste, chaudière, pompe à chaleur, chauffage Suisse",
    metaTitle: "Lead Artisan pour Chauffagistes — Plus d'installations, moins de temps perdu",
    metaDesc: "Solution IA pour chauffagistes en Suisse. Trouvez des projets d'installation et d'entretien. Call bot 24/7, relances automatiques.",
  },
  peintre: {
    title: "Peintre",
    hook: "On vous trouve des chantiers de peinture pendant que vous êtes au rouleau",
    subtitle: "Intérieur, extérieur, rénovation — des demandes qualifiées dans votre zone.",
    stats: { clients: "+8", label: "Projets/mois" },
    keywords: "peintre, peinture intérieure, rénovation peinture, peintre Suisse",
    metaTitle: "Lead Artisan pour Peintres — Des projets qualifiés dans votre zone",
    metaDesc: "Solution IA pour peintres en Suisse. Recevez des demandes qualifiées. Call bot, relances devis, CRM — tout est automatisé.",
  },
  menuisier: {
    title: "Menuisier",
    hook: "On vous trouve des projets sur mesure pendant que vous êtes à l'atelier",
    subtitle: "Cuisines, agencements, menuiserie — attirez les clients qui cherchent la qualité.",
    stats: { clients: "+6", label: "Projets/mois" },
    keywords: "menuisier, cuisine sur mesure, agencement, menuiserie Suisse",
    metaTitle: "Lead Artisan pour Menuisiers — Attirez les projets sur mesure",
    metaDesc: "Solution IA pour menuisiers en Suisse. Cuisines, agencements, projets haut de gamme. Acquisition clients et gestion automatisée.",
  },
  paysagiste: {
    title: "Paysagiste",
    hook: "On vous trouve des aménagements pendant que vous êtes dans le jardin",
    subtitle: "Création, entretien, aménagement extérieur — des clients ciblés dans votre zone.",
    stats: { clients: "+10", label: "Projets/mois" },
    keywords: "paysagiste, aménagement jardin, entretien espaces verts, paysagiste Suisse",
    metaTitle: "Lead Artisan pour Paysagistes — Des projets d'aménagement ciblés",
    metaDesc: "Solution IA pour paysagistes en Suisse. Trouvez des projets d'aménagement et d'entretien dans votre zone. Gestion automatisée.",
  },
  renovation: {
    title: "Rénovation",
    hook: "On vous trouve des projets de rénovation pendant que vous êtes sur chantier",
    subtitle: "Salle de bains, cuisine, appartement complet — captez les projets les plus rentables.",
    stats: { clients: "+5", label: "Projets/mois" },
    keywords: "rénovation, salle de bain, cuisine, rénovation appartement Suisse",
    metaTitle: "Lead Artisan pour la Rénovation — Captez les projets rentables",
    metaDesc: "Solution IA pour entreprises de rénovation en Suisse. Trouvez des projets qualifiés, gérez vos devis et relances automatiquement.",
  },
  carreleur: {
    title: "Carreleur",
    hook: "On vous trouve des chantiers de carrelage pendant que vous posez",
    subtitle: "Salle de bains, cuisine, terrasse — des demandes qualifiées dans votre zone.",
    stats: { clients: "+8", label: "Chantiers/mois" },
    keywords: "carreleur, carrelage, pose carrelage, carreleur Suisse",
    metaTitle: "Lead Artisan pour Carreleurs — Des chantiers qualifiés dans votre zone",
    metaDesc: "Solution IA pour carreleurs en Suisse. Trouvez des chantiers, répondez aux appels 24/7, relancez vos devis automatiquement.",
  },
  facadier: {
    title: "Façadier",
    hook: "On vous trouve des façades à rénover pendant que vous êtes sur l'échafaudage",
    subtitle: "Crépis, isolation, ravalement — remplissez votre planning de chantiers rentables.",
    stats: { clients: "+6", label: "Chantiers/mois" },
    keywords: "façadier, façade, crépis, isolation extérieure, ravalement Suisse",
    metaTitle: "Lead Artisan pour Façadiers — Remplissez votre planning de façades",
    metaDesc: "Solution IA pour façadiers en Suisse. Acquisition de chantiers, call bot 24/7, relances devis et factures automatiques.",
  },
  couvreur: {
    title: "Couvreur",
    hook: "On vous trouve des toitures à traiter pendant que vous êtes sur le toit",
    subtitle: "Réfection, étanchéité, entretien — captez chaque demande urgente.",
    stats: { clients: "+7", label: "Chantiers/mois" },
    keywords: "couvreur, toiture, étanchéité, réfection toit, couvreur Suisse",
    metaTitle: "Lead Artisan pour Couvreurs — Captez chaque demande de toiture",
    metaDesc: "Solution IA pour couvreurs en Suisse. Urgences et projets planifiés. Call bot 24/7, relances automatiques.",
  },
  ferblantier: {
    title: "Ferblantier",
    hook: "On vous trouve des chantiers de ferblanterie pendant que vous façonnez",
    subtitle: "Gouttières, zinguerie, étanchéité — des demandes ciblées dans votre rayon.",
    stats: { clients: "+5", label: "Chantiers/mois" },
    keywords: "ferblantier, gouttière, zinguerie, ferblanterie Suisse",
    metaTitle: "Lead Artisan pour Ferblantiers — Des chantiers ciblés dans votre rayon",
    metaDesc: "Solution IA pour ferblantiers en Suisse. Trouvez des demandes qualifiées et gérez vos relances automatiquement.",
  },
  vitrier: {
    title: "Vitrier",
    hook: "On vous trouve des interventions pendant que vous êtes en pose",
    subtitle: "Remplacement, double vitrage, vitrines — ne ratez plus aucune urgence.",
    stats: { clients: "+10", label: "Interventions/mois" },
    keywords: "vitrier, remplacement vitre, double vitrage, vitrier urgence Suisse",
    metaTitle: "Lead Artisan pour Vitriers — Ne ratez plus aucune urgence vitrerie",
    metaDesc: "Solution IA pour vitriers en Suisse. Urgences et projets. Call bot 24/7, relances devis, CRM automatisé.",
  },
  macon: {
    title: "Maçon",
    hook: "On vous trouve des chantiers de maçonnerie pendant que vous coulez le béton",
    subtitle: "Construction, rénovation, agrandissement — des projets qualifiés dans votre zone.",
    stats: { clients: "+4", label: "Projets/mois" },
    keywords: "maçon, maçonnerie, construction, rénovation maçonnerie Suisse",
    metaTitle: "Lead Artisan pour Maçons — Des projets qualifiés dans votre zone",
    metaDesc: "Solution IA pour maçons en Suisse. Trouvez des chantiers, gérez vos appels et relances automatiquement.",
  },
  platrier: {
    title: "Plâtrier",
    hook: "On vous trouve des chantiers de plâtrerie pendant que vous enduisez",
    subtitle: "Cloisons, faux plafonds, enduits — remplissez votre agenda de chantiers.",
    stats: { clients: "+6", label: "Chantiers/mois" },
    keywords: "plâtrier, plâtrerie, cloison, faux plafond, plâtrier Suisse",
    metaTitle: "Lead Artisan pour Plâtriers — Remplissez votre agenda de chantiers",
    metaDesc: "Solution IA pour plâtriers en Suisse. Acquisition clients, call bot 24/7, gestion devis et relances automatisées.",
  },
  photovoltaique: {
    title: "Photovoltaïque",
    hook: "On vous trouve des installations solaires pendant que vous êtes sur les toits",
    subtitle: "Panneaux, batteries, bornes de recharge — captez la demande croissante.",
    stats: { clients: "+8", label: "Projets/mois" },
    keywords: "photovoltaïque, panneaux solaires, installation solaire, énergie solaire Suisse",
    metaTitle: "Lead Artisan pour le Photovoltaïque — Captez la demande solaire",
    metaDesc: "Solution IA pour installateurs photovoltaïques en Suisse. Trouvez des projets d'installation solaire. Gestion automatisée.",
  },
};

/** Build ville-adapted override by merging metier + ville data */
function buildVilleOverride(metierData, villeData, metierImages, metierSlug) {
  const v = villeData;
  const m = metierData;
  const img = metierImages;
  const titre = m.title;

  return {
    ...m,
    // Adapted hook with city name
    hook: `${titre} à ${v.nom} — on vous trouve des clients pendant que vous travaillez`,
    subtitle: `${m.subtitle.replace(/\.$/, "")} à ${v.nom} et dans le canton de ${v.canton}.`,
    // SEO
    metaTitle: `${titre} à ${v.nom} — Plus de clients avec Lead Artisan`,
    metaDesc: `Solution IA pour ${titre.toLowerCase()}s à ${v.nom} (${v.canton}). ${v.description.split(".")[0]}. Call bot 24/7, relances automatiques.`,
    keywords: `${m.keywords}, ${titre.toLowerCase()} ${v.nom}, ${titre.toLowerCase()} ${v.canton}, artisan ${v.nom}`,
    // Ville data for Hero
    ville: v,
    // Metier images
    metierImage: img?.heroImage,
    metierImageAlt: img?.heroAlt,
    metierSpecialites: img?.specialites,
    metierProblemes: img?.problemesMetier,
  };
}

export default function MetierLandingPage() {
  const { metier, ville } = useParams();
  const metierKey = metier?.toLowerCase();
  const villeKey = ville?.toLowerCase();

  const metierData = METIER_DATA[metierKey];
  const villeData = villeKey ? VILLES[villeKey] : null;
  const metierImg = METIER_IMAGES[metierKey];

  // Build the override: ville-specific or metier-only
  const overrideData = metierData
    ? villeData
      ? { ...buildVilleOverride(metierData, villeData, metierImg, metierKey), _metierSlug: metierKey }
      : {
          ...metierData,
          _metierSlug: metierKey,
          metierImage: metierImg?.heroImage,
          metierImageAlt: metierImg?.heroAlt,
          metierSpecialites: metierImg?.specialites,
          metierProblemes: metierImg?.problemesMetier,
        }
    : null;

  useEffect(() => {
    if (!overrideData) return;
    document.title = overrideData.metaTitle;
    const setMeta = (sel, attr, val) => {
      const el = document.querySelector(sel);
      if (el) el.setAttribute(attr, val);
    };
    setMeta('meta[name="description"]', "content", overrideData.metaDesc);
    setMeta('meta[name="keywords"]', "content", overrideData.keywords);
    setMeta('meta[property="og:title"]', "content", overrideData.metaTitle);
    setMeta('meta[property="og:description"]', "content", overrideData.metaDesc);
    const urlPath = villeKey ? `/metier/${metier}/${ville}` : `/metier/${metier}`;
    setMeta('meta[property="og:url"]', "content", `https://lead-artisan.com${urlPath}`);
    setMeta('meta[name="twitter:title"]', "content", overrideData.metaTitle);
    setMeta('meta[name="twitter:description"]', "content", overrideData.metaDesc);

    // Add canonical tag
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", `https://lead-artisan.com${urlPath}`);

    // Add JSON-LD structured data for SEO
    const existingLd = document.getElementById("ld-metier-ville");
    if (existingLd) existingLd.remove();
    const ldScript = document.createElement("script");
    ldScript.id = "ld-metier-ville";
    ldScript.type = "application/ld+json";
    const villeInfo = overrideData.ville;
    ldScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      name: `${overrideData.title}${villeInfo ? ` à ${villeInfo.nom}` : " en Suisse romande"}`,
      description: overrideData.metaDesc,
      provider: {
        "@type": "Organization",
        name: "Lead Artisan",
        url: "https://lead-artisan.com",
      },
      ...(villeInfo && {
        areaServed: {
          "@type": "City",
          name: villeInfo.nom,
          containedInPlace: { "@type": "AdministrativeArea", name: `Canton de ${villeInfo.canton}` },
        },
      }),
      serviceType: overrideData.title,
      url: `https://lead-artisan.com${urlPath}`,
    });
    document.head.appendChild(ldScript);

    return () => {
      document.title = "Lead Artisan — 12 Solutions IA pour Artisans en Suisse";
      const ld = document.getElementById("ld-metier-ville");
      if (ld) ld.remove();
      if (canonical) canonical.remove();
    };
  }, [overrideData, metier, ville, villeKey]);

  // Invalid metier → redirect home
  if (!metierData) return <Navigate to="/" replace />;
  // Invalid ville → redirect to metier page (without ville)
  if (villeKey && !villeData) return <Navigate to={`/metier/${metier}`} replace />;

  return <LandingPage metierOverride={overrideData} />;
}

export { METIER_DATA };
